import {useEffect, useMemo, useRef, useState} from 'react';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import ReactDocumentTitle from '@/utils/title';
import globalStore from '@/stores/globalStore';
import metricCatStudy from '@/static/pic/catr0.png';
import UShouldKnowDetail from './UShouldKnowDetail';
import {resumeQuickEn} from './resumeContent';
import s from './quick.module.less';

const DEFAULT_LAYOUT = Object.freeze({
    topHeight: 18,
    bottomLeftWidth: 48,
    bottomRightTopHeight: 36,
});

const LAYOUT_LIMITS = Object.freeze({
    minTopHeight: 14,
    maxTopHeight: 48,
    minAboutHeight: 25,
    maxAboutHeight: 70,
});

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const highlightWords = (text, words, className, variantClassName = '') => {
    const source = String(text || '');
    const normalizedWords = Array.from(new Set((words || []).map((item) => String(item || '').trim()).filter(Boolean)));
    if (!normalizedWords.length) {
        return source;
    }
    const pattern = new RegExp(`(${normalizedWords.map((item) => item.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).sort((a, b) => b.length - a.length).join('|')})`, 'gi');
    const normalizedSet = new Set(normalizedWords.map((item) => item.toLowerCase()));
    const parts = source.split(pattern).filter((part) => part !== '');
    let matchIndex = 0;

    return parts.map((part, idx) => {
        if (!normalizedSet.has(part.toLowerCase())) {
            return <span key={`plain-${idx}`}>{part}</span>;
        }
        const inkDelay = `${Math.min(matchIndex * 90, 720)}ms`;
        matchIndex += 1;
        return <span
            key={`highlight-${idx}-${part}`}
            className={`${className} ${variantClassName}`.trim()}
            style={{'--ink-delay': inkDelay}}
        >{part}</span>;
    });
};

const buildBackgroundSkills = (skills, highlightSkills, cols, rows) => {
    const normalizedSkills = Array.isArray(skills) && skills.length ? skills : ['Skill'];
    const highlightSet = new Set((highlightSkills || []).map((item) => String(item || '').trim()));
    const nodes = [];
    for (let row = 0; row < rows; row += 1) {
        for (let col = 0; col < cols; col += 1) {
            const index = (row * cols) + col;
            const name = normalizedSkills[index % normalizedSkills.length];
            const jitterX = (((index % 5) - 2) * 0.12);
            const jitterY = ((((index + 3) % 7) - 3) * 0.12);
            const x = clamp((((col + 0.5) / cols) * 100) + jitterX, 1.4, 98.6);
            const y = clamp((((row + 0.5) / rows) * 100) + jitterY, 1.2, 98.8);
            nodes.push({
                key: `${name}-${index}`,
                name,
                x,
                y,
                idx: index,
                accent: highlightSet.has(name),
            });
        }
    }
    return nodes;
};

const CONTENT = Object.freeze({en: resumeQuickEn});

function UShouldKnowQuick() {
    const {lang} = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const containerRef = useRef(null);
    const [layout, setLayout] = useState(DEFAULT_LAYOUT);
    const [dragging, setDragging] = useState(null);
    const [selectedDimension, setSelectedDimension] = useState('strategy');
    const [isIntroReady, setIsIntroReady] = useState(false);
    const [viewportWidth, setViewportWidth] = useState(typeof window === 'undefined' ? 1440 : window.innerWidth);
    const [mousePoint, setMousePoint] = useState({x: 50, y: 50, active: false});

    const safeLang = useMemo(() => {
        if (lang === 'en' || lang === 'ja' || lang === 'zh') {
            return lang;
        }
        return 'zh';
    }, [lang]);

    const copy = CONTENT.en;
    const activeDimension = copy.leadershipDimensions.find((item) => item.id === selectedDimension) || copy.leadershipDimensions[0];
    const isDetailMode = location.pathname.endsWith('/detail') || new URLSearchParams(location.search).get('view') === 'detail';
    const backgroundSkills = useMemo(() => {
        const compact = viewportWidth <= 840;
        const cols = compact ? 10 : viewportWidth <= 1280 ? 16 : 20;
        const rows = compact ? 22 : 15;
        return buildBackgroundSkills(copy.skills, copy.skillHighlights, cols, rows);
    }, [copy.skills, copy.skillHighlights, viewportWidth]);

    const toDetail = () => {
        navigate(`/ushouldknow/${safeLang}/detail`);
    };

    const startDrag = (type) => (event) => {
        event.preventDefault();
        setDragging(type);
    };

    const handleCanvasMouseMove = (event) => {
        if (!containerRef.current) {
            return;
        }
        const rect = containerRef.current.getBoundingClientRect();
        if (!rect.width || !rect.height) {
            return;
        }
        const x = clamp(((event.clientX - rect.left) / rect.width) * 100, 0, 100);
        const y = clamp(((event.clientY - rect.top) / rect.height) * 100, 0, 100);
        setMousePoint({x, y, active: true});
    };

    const handleCanvasMouseLeave = () => {
        setMousePoint((prev) => ({...prev, active: false}));
    };

    useEffect(() => {
        if (typeof window === 'undefined') {
            return undefined;
        }
        const handleResize = () => {
            setViewportWidth(window.innerWidth);
        };
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    useEffect(() => {
        const raf = window.requestAnimationFrame(() => {
            setIsIntroReady(true);
        });
        return () => {
            window.cancelAnimationFrame(raf);
        };
    }, []);

    useEffect(() => {
        if (!dragging || !containerRef.current) {
            return undefined;
        }

        const handleMouseMove = (event) => {
            if (!containerRef.current) {
                return;
            }
            const rect = containerRef.current.getBoundingClientRect();

            setLayout((prev) => {
                const next = {...prev};

                if (dragging === 'horizontal-main') {
                    const nextTopHeight = ((event.clientY - rect.top) / rect.height) * 100;
                    next.topHeight = clamp(nextTopHeight, LAYOUT_LIMITS.minTopHeight, LAYOUT_LIMITS.maxTopHeight);
                } else if (dragging === 'vertical-bottom') {
                    const nextBottomLeftWidth = ((event.clientX - rect.left) / rect.width) * 100;
                    next.bottomLeftWidth = clamp(nextBottomLeftWidth, 30, 70);
                } else if (dragging === 'horizontal-bottom-right') {
                    const topOffset = (prev.topHeight / 100) * rect.height;
                    const bottomHeight = rect.height - topOffset;
                    const mouseOffset = event.clientY - rect.top - topOffset;
                    const nextAboutHeight = (mouseOffset / bottomHeight) * 100;
                    next.bottomRightTopHeight = clamp(nextAboutHeight, LAYOUT_LIMITS.minAboutHeight, LAYOUT_LIMITS.maxAboutHeight);
                }

                return next;
            });
        };

        const handleMouseUp = () => {
            setDragging(null);
        };

        const cursorType = dragging.includes('horizontal') ? 'row-resize' : 'col-resize';
        document.body.style.cursor = cursorType;
        document.body.style.userSelect = 'none';
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);

        return () => {
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [dragging]);

    if (isDetailMode) {
        return <UShouldKnowDetail initialView='detail' lockView/>;
    }

    return <ReactDocumentTitle title={`${globalStore.webSiteTitle} - UShouldKnow Quick`}>
        <main className={s.page}>
            <section className={`${s.canvas} ${isIntroReady ? s.canvasIntroReady : ''}`}>
                <div ref={containerRef} className={s.canvasBody} onMouseMove={handleCanvasMouseMove} onMouseLeave={handleCanvasMouseLeave}>
                    <div className={s.skillsBackground} aria-hidden>
                        {backgroundSkills.map((item) => {
                            const radius = viewportWidth <= 840 ? 18 : 10;
                            const distance = Math.hypot(item.x - mousePoint.x, item.y - mousePoint.y);
                            const glow = mousePoint.active ? clamp(1 - (distance / radius), 0, 1) : 0;
                            return <span
                                key={item.key}
                                className={`${s.bgSkill} ${item.accent ? s.bgSkillAccent : ''} ${glow > 0 ? s.bgSkillActive : ''}`}
                                style={{left: `${item.x}%`, top: `${item.y}%`, '--glow': glow.toFixed(3), '--seed': `${item.idx % 4}`}}
                            >{item.name}</span>;
                        })}
                    </div>

                    <div className={s.topSection} style={{height: `${layout.topHeight}%`}}>
                        <article className={`${s.panel} ${s.heroPanel}`} style={{width: '100%'}}>
                            <h1 className={s.heroHeading}>
                                <span>{copy.heroTitle[0]}</span>
                                <span>{copy.heroTitle[1]}</span>
                            </h1>
                            <p className={s.heroSubline}>{highlightWords(copy.positioning, copy.positioningHighlights, s.highlightInk, s.highlightInkWarm)}</p>
                        </article>
                    </div>

                    <button
                        type='button'
                        className={`${s.dividerHorizontal} ${s.dividerMain} ${dragging === 'horizontal-main' ? s.dividerActive : ''}`}
                        style={{top: `${layout.topHeight}%`}}
                        onMouseDown={startDrag('horizontal-main')}
                        aria-label='Resize top and bottom panels'
                    >
                        <span className={s.dividerLineHorizontal}/>
                    </button>

                    <div className={s.bottomSection} style={{height: `${100 - layout.topHeight}%`}}>
                        <article className={`${s.panel} ${s.workPanel}`} style={{width: `${layout.bottomLeftWidth}%`}}>
                            <h2 className={s.sectionTitle}>{copy.workTitle}</h2>
                            <p className={s.metaText}>{copy.workCate}</p>
                            <p className={s.leadershipMandate}>{copy.leadershipMandate.map((line) => <span key={line}>{line}</span>)}</p>
                            <div className={s.decisionSelector} role='group' aria-label='Leadership decision dimensions'>
                                {copy.leadershipDimensions.map((item) => <button
                                    key={item.id}
                                    type='button'
                                    aria-pressed={item.id === activeDimension.id}
                                    aria-controls='leadership-decision-detail'
                                    onClick={() => setSelectedDimension(item.id)}
                                >
                                    <strong>{item.label}</strong>
                                    <span>{item.caption}</span>
                                </button>)}
                            </div>
                            <div id='leadership-decision-detail' className={s.decisionDetail} role='region' aria-label='Selected leadership responsibility' aria-live='polite'>
                                <span>{activeDimension.title}</span>
                                <h3>{activeDimension.question}</h3>
                                <p>{activeDimension.description}</p>
                            </div>
                            <section className={s.technicalLeadership} aria-labelledby='technical-leadership-heading'>
                                <div className={s.technicalLeadershipHeading}>
                                    <h3 id='technical-leadership-heading'>{copy.technicalLeadershipTitle}</h3>
                                    <p>{copy.technicalLeadershipIntro}</p>
                                </div>
                                <ul>{copy.technicalResponsibilities.map((item) => <li key={item.title}>
                                    <h4>{item.title}</h4>
                                    <p>{item.description}</p>
                                </li>)}</ul>
                            </section>
                            <a className={s.modelLink} href='/ushouldknow/zh/detail#capability-model'>Explore capabilities & evidence <span aria-hidden='true'>↗</span></a>
                        </article>

                        <button
                            type='button'
                            className={`${s.dividerVertical} ${dragging === 'vertical-bottom' ? s.dividerActive : ''}`}
                            onMouseDown={startDrag('vertical-bottom')}
                            aria-label='Resize bottom panels'
                        >
                            <span className={s.dividerLineVertical}/>
                        </button>

                        <div className={s.bottomRightSection} style={{width: `${100 - layout.bottomLeftWidth}%`}}>
                            <article className={`${s.panel} ${s.aboutPanel}`} style={{height: `${layout.bottomRightTopHeight}%`}}>
                                <h2 className={s.sectionTitle}>{copy.aboutTitle}</h2>
                                <div className={s.aboutBody}>
                                    <button type='button' className={s.aboutAvatarBtn} onClick={toDetail} aria-label='Open detail page'>
                                        <img src={metricCatStudy} alt='avatar' className={s.aboutAvatar}/>
                                    </button>
                                    <div className={s.aboutCopy}>
                                        <h3 className={s.aboutMission}>{copy.aboutMission}</h3>
                                        <p>{highlightWords(copy.aboutText, copy.aboutHighlights, s.highlightInk, s.highlightInkCool)}</p>
                                        <p className={s.personalIdentity}>{copy.aboutPersonal}</p>
                                    </div>
                                </div>
                            </article>

                            <button
                                type='button'
                                className={`${s.dividerHorizontal} ${s.dividerSub} ${dragging === 'horizontal-bottom-right' ? s.dividerActive : ''}`}
                                style={{top: `${layout.bottomRightTopHeight}%`}}
                                onMouseDown={startDrag('horizontal-bottom-right')}
                                aria-label='Resize about and contact panels'
                            >
                                <span className={s.dividerLineHorizontal}/>
                            </button>

                            <article
                                className={`${s.panel} ${s.experiencePanel}`}
                                style={{
                                    top: `${layout.bottomRightTopHeight}%`,
                                    height: `calc((100% - ${layout.bottomRightTopHeight}%) * 0.86)`,
                                }}
                            >
                                <h3 className={s.experienceTitle}>{copy.experienceTitle}</h3>
                                <div className={s.experienceRail}>
                                    <div className={s.experienceList}>
                                        {copy.experiences.map((item, index) => <details key={item.company} className={s.experienceItem} open={index < 2}>
                                            <summary className={s.experienceHead}>
                                                <strong>{item.company}</strong>
                                                <span>{item.period}</span>
                                            </summary>
                                            <p>{highlightWords(item.summary, item.highlights, s.highlightInk, s.highlightInkCool)}</p>
                                            {Array.isArray(item.focus) && item.focus.length ? <div className={s.experienceFocus}>
                                                {item.focus.map((focus) => <span key={`${item.company}-${focus}`}>{focus}</span>)}
                                            </div> : null}
                                        </details>)}
                                    </div>
                                </div>
                            </article>

                            <article
                                className={`${s.panel} ${s.contactPanel}`}
                                style={{
                                    top: `calc(${layout.bottomRightTopHeight}% + (100% - ${layout.bottomRightTopHeight}%) * 0.86)`,
                                    height: `calc((100% - ${layout.bottomRightTopHeight}%) * 0.14)`,
                                }}
                            >
                                <h2 className={`${s.sectionTitle} ${s.contactTitle}`}>{copy.contactTitle}</h2>
                                <a href={`mailto:${copy.contact}`} className={s.contactLink}>{copy.contact}</a>
                            </article>
                        </div>
                    </div>
                </div>
            </section>
        </main>
    </ReactDocumentTitle>;
}

export default UShouldKnowQuick;
